import React from "react"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { useProjectNavStore } from "stores/projectNavStore"
import {
  FALLBACK_SECTION_ICON,
  PROJECT_SECTION_ICONS,
} from "constants/projectSectionIcons"

interface ProjectSectionNavProps {
  /** When true the sidebar is folded: icons only, the name in a tooltip */
  collapsed: boolean
}

/**
 * The open project's sections as a vertical tab list in the sidebar.
 *
 * Reads the sections ProjectDetailsTabs publishes (projectNavStore) and calls
 * its `select` on a click, so the `?tab=` and Back handling stays in one place.
 * Renders nothing when no project is open.
 *
 * @param props - The props for the component
 * @returns The section list, or null outside a project
 */
const ProjectSectionNav: React.FC<ProjectSectionNavProps> = ({ collapsed }) => {
  const sections = useProjectNavStore((state) => state.sections)
  const activeKey = useProjectNavStore((state) => state.activeKey)
  const select = useProjectNavStore((state) => state.select)

  if (!sections) return null

  return (
    <div
      role="tablist"
      aria-orientation="vertical"
      aria-label="Project sections"
      className="flex flex-col gap-0.5"
    >
      {sections.map((section) => {
        const active = section.key === activeKey
        const row = (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={collapsed ? section.label : undefined}
            onClick={() => select?.(section.key)}
            className={`sidebar-row ${active ? "sidebar-row--active" : ""} ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <FontAwesomeIcon
              className="sidebar-row-icon"
              icon={PROJECT_SECTION_ICONS[section.key] ?? FALLBACK_SECTION_ICON}
              fixedWidth
            />
            {!collapsed && <span className="truncate">{section.label}</span>}
          </button>
        )
        return collapsed ? (
          <Tooltip key={section.key} title={section.label} placement="right">
            {row}
          </Tooltip>
        ) : (
          row
        )
      })}
    </div>
  )
}

export default ProjectSectionNav
